import { useLayoutEffect, useRef, useState } from 'react';

/**
 * Renders a row of pill/tab buttons that wraps up to 2 visual lines.
 * If the row would need a 3rd line at the current available width, it
 * collapses into a native <select> dropdown instead (never more than 2 lines).
 *
 * items: [{ key, label, active, onSelect, title? }]
 *
 * Measurement approach: a hidden clone of the button row is kept mounted
 * (absolutely positioned, width pinned to the wrapper's real width via
 * ResizeObserver) so we always know how tall the row *would* be at the
 * current width, in both row-mode and dropdown-mode. If that height is
 * greater than 2 lines (2 * single-button height + row-gap, plus a small
 * rounding tolerance), we render the dropdown; otherwise we render the row.
 */
function WrapOrDropdown({ items, rowClassName = '', selectClassName = '', ariaLabel, wrapperStyle }) {
    const wrapperRef = useRef(null);
    const measureRef = useRef(null);
    const [useDropdown, setUseDropdown] = useState(false);
    const [measureWidth, setMeasureWidth] = useState(0);

    // Track the width available to the row/select (follows the wrapper,
    // which stays in the flex flow regardless of which mode is active).
    useLayoutEffect(() => {
        const wrapper = wrapperRef.current;
        if (!wrapper) return undefined;
        const update = () => setMeasureWidth(wrapper.offsetWidth);
        update();
        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', update);
            return () => window.removeEventListener('resize', update);
        }
        const ro = new ResizeObserver(update);
        ro.observe(wrapper);
        return () => ro.disconnect();
    }, []);

    // Decide row vs dropdown by measuring the hidden clone at that width.
    useLayoutEffect(() => {
        const el = measureRef.current;
        if (!el || !items || items.length === 0 || !measureWidth) {
            setUseDropdown(false);
            return;
        }
        const firstButton = el.querySelector('button');
        if (!firstButton) {
            setUseDropdown(false);
            return;
        }
        const styles = window.getComputedStyle(el);
        const rowGap = parseFloat(styles.rowGap || styles.gap || '0') || 0;
        const lineHeight = firstButton.offsetHeight;
        const maxTwoLineHeight = lineHeight * 2 + rowGap + 2; // +2px rounding tolerance
        setUseDropdown(el.scrollHeight > maxTwoLineHeight);
    }, [items, measureWidth]);

    if (!items || items.length === 0) return null;

    const activeItem = items.find((item) => item.active);

    return (
        <div ref={wrapperRef} style={{ position: 'relative', minWidth: 0, ...wrapperStyle }}>
            {/* Hidden measurer — never shown, only used to detect 3+ line wraps */}
            <div
                ref={measureRef}
                className={rowClassName}
                aria-hidden="true"
                style={{
                    position: 'absolute',
                    visibility: 'hidden',
                    pointerEvents: 'none',
                    top: 0,
                    left: 0,
                    width: measureWidth ? `${measureWidth}px` : 'auto',
                }}
            >
                {items.map((item) => (
                    <button key={item.key} type="button" className={item.active ? 'active' : ''} tabIndex={-1}>
                        {item.label}
                    </button>
                ))}
            </div>

            {useDropdown ? (
                <select
                    className={selectClassName}
                    aria-label={ariaLabel}
                    value={activeItem ? String(activeItem.key) : ''}
                    onChange={(event) => {
                        const item = items.find((candidate) => String(candidate.key) === event.target.value);
                        if (item) item.onSelect();
                    }}
                >
                    {items.map((item) => (
                        <option key={item.key} value={item.key}>{item.label}</option>
                    ))}
                </select>
            ) : (
                <div className={rowClassName}>
                    {items.map((item) => (
                        <button
                            key={item.key}
                            type="button"
                            className={item.active ? 'active' : ''}
                            onClick={item.onSelect}
                            title={item.title}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

export default WrapOrDropdown;
